/**
 * Timeline Utilities
 *
 * Extracted from Timeline2.tsx to enable proper unit testing
 * and reuse across the codebase.
 *
 * Contains pure functions for:
 * - Comparing reports and generating deltas
 * - Grouping individual reports into report groups
 * - Date/time formatting for timeline entries
 */

import type { IndividualReportEntry, ReportGroup } from 'src/types/appCalcTypes'

import { dedupAccountDataToLatestLink } from './assetTransactionUtils'

// Re-export getOverrideOptionLabel from overrideUtils for backwards compatibility
export { getOverrideOptionLabel } from './overrideUtils'

// =============================================================================
// Date/Time Formatting Utilities
// =============================================================================

const relativeTimeFormat = new Intl.RelativeTimeFormat('en-US', {
  localeMatcher: 'best fit',
  numeric: 'always',
  style: 'long',
})

/**
 * Calculates relative time from now (e.g., "5 minutes ago", "in 2 days")
 *
 * @param timestamp - The timestamp to compare against now
 * @returns Formatted relative time string
 *
 * @example
 * getRelativeTimeFromNow('2024-01-15T10:00:00Z') // "3 days ago"
 */
export function getRelativeTimeFromNow(timestamp: string | Date): string {
  const now = new Date()
  const date = new Date(timestamp)
  const diffMs = date.getTime() - now.getTime()
  const diffSecs = Math.round(diffMs / 1000)
  const diffMins = Math.round(diffSecs / 60)
  const diffHours = Math.round(diffMins / 60)
  const diffDays = Math.round(diffHours / 24)
  const diffMonths = Math.round(diffDays / 30)
  const diffYears = Math.round(diffMonths / 12)

  if (Math.abs(diffSecs) < 60) {
    return relativeTimeFormat.format(diffSecs, 'second')
  } else if (Math.abs(diffMins) < 60) {
    return relativeTimeFormat.format(diffMins, 'minute')
  } else if (Math.abs(diffHours) < 24) {
    return relativeTimeFormat.format(diffHours, 'hour')
  } else if (Math.abs(diffDays) < 30) {
    return relativeTimeFormat.format(diffDays, 'day')
  } else if (Math.abs(diffMonths) < 12) {
    return relativeTimeFormat.format(diffMonths, 'month')
  } else {
    return relativeTimeFormat.format(diffYears, 'year')
  }
}

/**
 * Formats a timestamp with full weekday, date, time, and relative time
 *
 * @param timestamp - The timestamp to format
 * @returns Formatted string like "Monday, January 15, 10:30:45 AM (3 days ago)"
 *
 * @example
 * formatTimestamp('2024-01-15T10:30:45Z') // "Monday, January 15, 10:30:45 AM (3 days ago)"
 */
export function formatTimestamp(
  timestamp: string | Date | null | undefined
): string {
  if (!timestamp) return ''
  const formatted = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(timestamp))
  const relative = getRelativeTimeFromNow(timestamp)
  return `${formatted} (${relative})`
}

/**
 * Formats a timestamp in compact form (date and time only)
 *
 * @param timestamp - The timestamp to format
 * @returns Formatted string like "1/15, 10:30:45 AM"
 *
 * @example
 * formatTimestampCompact('2024-01-15T10:30:45Z') // "1/15, 10:30:45 AM"
 */
export function formatTimestampCompact(timestamp: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(timestamp))
}

/**
 * Formats a date string for display in timeline entries
 *
 * @param dateString - ISO date string to format
 * @returns Formatted string like "Jan 15, 2024, 10:30 AM"
 *
 * @example
 * formatTimelineDate('2024-01-15T10:30:00Z') // "Jan 15, 2024, 10:30 AM"
 */
export function formatTimelineDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// =============================================================================
// Report Comparison and Grouping
// =============================================================================

/**
 * Compares two reports and returns a delta object showing what changed.
 *
 * For the first report (no previous), returns '-' markers for fields that have values.
 * For subsequent reports, returns the previous values for fields that changed.
 *
 * @param previous - The previous report entry, or null if this is the first
 * @param current - The current report entry
 * @returns Object with changed field names as keys and previous values (or '-') as values
 */
export function compareReports(
  previous: IndividualReportEntry | null,
  current: IndividualReportEntry
): Record<string, unknown> {
  const keys = [
    'monthlyIncomeCents',
    'explanation',
    'fraudExplanation',
  ] as const
  const delta: Record<string, unknown> = {}
  for (const key of keys) {
    if (!previous) {
      const currentValue = current?.ReportVoi[key]
      if (currentValue) {
        delta[key] = '-'
      }
    } else {
      const previousValue = previous?.ReportVoi[key]
      const currentValue = current?.ReportVoi[key]
      if (previousValue !== currentValue) {
        delta[key] = previousValue || '-'
      }
    }
  }
  return delta
}

/**
 * Groups individual report entries into ReportGroups.
 *
 * Reports without an applicant become applicationReports.
 * Reports with an applicant are grouped together until the same applicant
 * appears again, which starts a new group.
 *
 * @param reports - Array of individual report entries to group
 * @returns Array of ReportGroups
 */
export function groupsFromReports(
  reports: IndividualReportEntry[]
): ReportGroup[] {
  const groups: ReportGroup[] = []

  for (const report of reports) {
    if (!report.applicant) {
      groups.push({
        applicationReport: {
          createdAt: report.ReportVoi.createdAt,
          timestamp: report.timestamp,
          ReportVoi: report.ReportVoi,
        },
        applicantReports: [],
      })
      continue
    }
    if (groups.length === 0) {
      groups.push({
        applicationReport: null,
        applicantReports: [report],
      })
    } else {
      // ensure we are not adding a report for the same applicant to the same group
      const already = groups[groups.length - 1].applicantReports.find(
        (ar) => ar.applicant.id === report.applicant.id
      )
      if (!already) {
        groups[groups.length - 1].applicantReports.push(report)
      } else {
        groups.push({
          applicationReport: null,
          applicantReports: [report],
        })
      }
    }
  }
  return groups
}

// =============================================================================
// Bank Link Alert Processing
// =============================================================================

/**
 * Account data from a bank link connection
 */
export interface BankLinkAccountData {
  currentBalanceUsd: string
  accountType: string
  accountMask: string
  accountId?: string
  institution: string
  success: boolean
  aplName?: string
  providerIncomeId?: number | null
}

/**
 * Overview of bank link accounts for an applicant
 */
export interface BankLinkAccountOverview {
  accountData: BankLinkAccountData[]
  applicantId: number
}

/**
 * Applicant data for bank link processing
 */
export interface BankLinkApplicant {
  id: number
  firstName: string
  lastName: string
}

/**
 * Result of processing bank link alert data
 */
export interface BankLinkAlertResult {
  showAlert: boolean
  failedAccts: (BankLinkAccountData & { applicantName: string })[]
  aplsWithFailedAccounts: string[]
}

/**
 * Input for formatBankLinkAlertData - accepts either app object or applicants array
 */
export interface FormatBankLinkAlertDataInput {
  bankLinkAccountOverview: BankLinkAccountOverview[] | undefined
  /** App object containing nested Applicant array - uses any for GraphQL type compatibility */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  app?: any
  applicants?: BankLinkApplicant[]
}

/**
 * Processes bank link account data to identify failed accounts that need alerts.
 *
 * Filters out credit accounts from failure alerts since they are expected to
 * fail in some cases. Deduplicates failed accounts by accountMask.
 *
 * @param bankLinkAccountOverview - Array of account overviews per applicant
 * @param app - Optional app object containing applicants (for backward compatibility)
 * @param applicants - Optional array of applicants (alternative to app)
 * @returns Object with showAlert flag, failed accounts, and affected applicant names
 *
 * @example
 * const result = formatBankLinkAlertData({
 *   bankLinkAccountOverview: [{ applicantId: 1, accountData: [...] }],
 *   applicants: [{ id: 1, firstName: 'John', lastName: 'Doe' }]
 * })
 * // { showAlert: true, failedAccts: [...], aplsWithFailedAccounts: ['John Doe'] }
 */
export function formatBankLinkAlertData({
  bankLinkAccountOverview,
  app,
  applicants,
}: FormatBankLinkAlertDataInput): BankLinkAlertResult {
  // Support both app object (backward compat) and direct applicants array
  const apls: BankLinkApplicant[] | undefined =
    applicants ?? app?.application?.Applicant
  const accountData = bankLinkAccountOverview?.flatMap((applicant) => {
    const flattened = applicant?.accountData?.flatMap((account) => {
      const apl = apls?.find((a) => a?.id === applicant?.applicantId)
      return [
        {
          ...account,
          applicantName: apl
            ? `${apl?.firstName as string} ${apl?.lastName as string}`
            : '??',
        },
      ]
    })
    return flattened
  })

  if (!accountData?.length) {
    return {
      showAlert: false,
      failedAccts: [],
      aplsWithFailedAccounts: [],
    }
  }

  // Drop older links so a re-linked account doesn't re-surface as failed.
  const latestAccountData = dedupAccountDataToLatestLink(accountData)

  const failedAcctsMap = new Map<
    string,
    BankLinkAccountData & { applicantName: string }
  >()
  for (const account of latestAccountData) {
    if (!account.success && account.accountType !== 'credit') {
      const key = account.accountId || account.accountMask
      if (!failedAcctsMap.has(key)) {
        failedAcctsMap.set(key, account)
      }
    }
  }
  const failedAccts = Array.from(failedAcctsMap.values())

  // Get unique applicant names from failed accounts
  const aplsWithFailedAccounts = [
    ...new Set(failedAccts.map((account) => account.applicantName)),
  ]

  const showAlert = failedAccts.length > 0

  return {
    showAlert,
    failedAccts,
    aplsWithFailedAccounts,
  }
}
